'use client';

import { motion } from 'framer-motion';
import { stegaClean } from '@sanity/client/stega';

import { cn } from '@/lib/utils';
import { IBlogPostCard } from '@/sanity/lib/definitions';
import { containerVariants, itemVariants } from '@/hooks/useEntranceAnimation';
import { Carousel, CarouselContent, CarouselItem } from '@/shadcn/ui/carousel';
import { useCarouselApi } from '@/hooks/useCarouselApi';
import BlogCard from './BlogCard';

const LABEL: Record<string, string> = {
  en: 'You might also like',
  de: 'Das könnte dich auch interessieren',
  br: 'Você também pode gostar',
};

const NAV_LABEL: Record<string, { prev: string; next: string }> = {
  en: { prev: 'Previous slide', next: 'Next slide' },
  de: { prev: 'Vorherige Folie', next: 'Nächste Folie' },
  br: { prev: 'Slide anterior', next: 'Próximo slide' },
};

export default function BlogRelatedPosts({
  posts,
  title,
  language = 'en',
  className,
}: {
  posts: IBlogPostCard[];
  title?: string;
  language?: string;
  className?: string;
}) {
  const { setApi, current, count, handleNext, handlePrev, isFirst, isLast } =
    useCarouselApi();

  if (!posts?.length) return null;

  const progress = count > 0 ? current / count : 1 / posts.length;
  const heading = title || LABEL[stegaClean(language) ?? ''] || LABEL.en;
  const navLabel = NAV_LABEL[stegaClean(language) ?? ''] ?? NAV_LABEL.en;

  return (
    <section
      className={cn('flex w-full flex-col items-center max-lg:pb-[10rem] lg:pb-[18.625rem]', className)}
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="mx-auto flex flex-col items-center max-lg:w-[min(176.375rem,calc(100%-4rem))] max-lg:gap-[3rem] max-lg:border-t max-lg:border-black/10 max-lg:pt-[7rem] lg:w-[min(176.375rem,calc(100%-8rem))] lg:gap-[6.375rem]"
      >
        <motion.h2
          variants={itemVariants}
          className="text-center font-geist font-medium leading-[1.15] text-black max-lg:text-[4.5rem] max-lg:tracking-[-0.225rem] lg:text-[5.5rem] lg:tracking-[-0.275rem]"
        >
          {heading}
        </motion.h2>

        <div className="flex w-full flex-col gap-[3rem]">
          <Carousel
            setApi={setApi}
            opts={{
              align: 'start',
              breakpoints: { '(min-width: 993px)': { active: false } },
            }}
            className="max-lg:-ml-[2rem] max-lg:w-[calc(100%+2rem)] lg:-ml-[4rem] lg:w-[calc(100%+4rem)]"
          >
            <CarouselContent className="lg:!overflow-visible lg:justify-center">
              {posts.map((post) => (
                <CarouselItem
                  key={post._id}
                  className="max-lg:basis-full lg:basis-1/3 lg:!pl-[4rem]"
                >
                  <motion.div variants={itemVariants} className="h-full">
                    <BlogCard post={post} language={language} />
                  </motion.div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {posts.length > 1 && (
            <div className="flex items-center gap-[2.5rem] lg:hidden">
              <button
                onClick={handlePrev}
                disabled={isFirst}
                className="flex h-[5rem] w-[5rem] flex-shrink-0 items-center justify-center rounded-[1rem] bg-[#c2ded6] transition-opacity disabled:opacity-40"
                aria-label={navLabel.prev}
              >
                <svg
                  viewBox="0 0 7.125 12.5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-[1.5rem] w-[0.875rem] rotate-180"
                >
                  <path
                    d="M7.125 6.25L0.875 12.5L0 11.625L5.375 6.25L0 0.875L0.875 0L7.125 6.25Z"
                    fill="#161616"
                  />
                </svg>
              </button>

              <div className="h-[0.25rem] flex-1 bg-[rgba(109,169,145,0.2)]">
                <div
                  className="h-full bg-[#228e71] transition-all duration-300"
                  style={{ width: `${progress * 100}%` }}
                />
              </div>

              <button
                onClick={handleNext}
                disabled={isLast}
                className="flex h-[5rem] w-[5rem] flex-shrink-0 items-center justify-center rounded-[1rem] bg-[#003828] transition-opacity disabled:opacity-40"
                aria-label={navLabel.next}
              >
                <svg
                  viewBox="0 0 7.125 12.5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-[1.5rem] w-[0.875rem]"
                >
                  <path
                    d="M7.125 6.25L0.875 12.5L0 11.625L5.375 6.25L0 0.875L0.875 0L7.125 6.25Z"
                    fill="white"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </section>
  );
}
